import { Box, ClickAwayListener, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid'
import { EventRecurrenceProps } from 'components/eventComponents/EventRecurrence'
import { PopupInstance } from 'components/popup/Popup'
import { ChangeEvent, ReactNode, useState } from 'react'
import { PopupRowTitle, SmallTextField } from '../styled'
import * as CalendarStyled from '../../../components/calendar/styled'
import { Option } from 'types/components/form'
import dayjs, { Dayjs } from 'dayjs'
import DatePickPopup from './DatePickPopup'
import Button from 'components/buttons/Button'
import { CustomEventRecurrencePopup } from './styled'
import { PopupSectionTitle } from 'components/popup/styled'
import { useTranslation } from 'react-i18next'
import { t } from 'i18next'
import { IEventDetails } from 'hooks/useEventDetails'
import { isCypress } from 'utils/cypress'
import { UserSettingsDoc } from 'types/api/changes'
import { FIRST_DAYS } from 'components/calendar/CalendarView'
import { useAppSettings } from 'components/providers/AppSettingsProvider'
import { getDefaultTimezone } from 'index'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { AppUIType } from 'components/providers/AppUITypeProvider'
import { pick } from 'lodash'

const UNTIL: Option[] = [
  {
    label: t('forever'),
    value: `${t('forever')}`
  },
  {
    label: `${t('until')}`,
    value: `${t('until')}`
  }
]

interface RecurrencePopupProps extends EventRecurrenceProps {
  open: boolean
  setOpen: (b: boolean) => void
  children: ReactNode
  eventHook: IEventDetails
  userSettings?: UserSettingsDoc
}

function Content(props: RecurrencePopupProps) {
  const theme = useTheme()
  const [interval, setInterval] = useState<string>(
    String(props.recurrence?.Interval ?? '1')
  )
  const onIntervalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    if (value.length <= 3) {
      setInterval(value)
    }
  }

  const [until, setUntil] = useState<string>(
    props.recurrence?.EndDate || isCypress()
      ? `${t('until')}`
      : `${t('forever')}`
  )

  const [unit, setUnit] = useState<string>(
    String(props.recurrence?.Unit ?? (isCypress() ? '1' : '2'))
  )

  let endDate = props.recurrence?.EndDate || null

  if (isCypress()) {
    const dayjsFirstDay =
      props.userSettings?.FirstDayOfTheWeek === FIRST_DAYS.SUNDAY ? 0 : 1

    const today = dayjs().startOf('day')
    const daysSinceFirstDay = (today.day() - dayjsFirstDay + 7) % 7

    let pickDate =
      today.day() === dayjsFirstDay
        ? today
        : today.subtract(daysSinceFirstDay, 'day')

    if (props.eventHook.getValues('AllDay') === '1') {
      pickDate = dayjs.tz(pickDate.format('YYYY-MM-DD'), 'UTC')
    }

    let pickEnd = pickDate.add(5, 'day')

    if (props.eventHook.getValues('AllDay') !== '1') {
      pickEnd = pickEnd.endOf('day')
    }

    endDate = pickEnd.unix().toString()

    if (
      LocalStorage.get(LocalStorageKey.CURRENT_UI) !== AppUIType.calendars &&
      (LocalStorage.get(LocalStorageKey.CURRENT_UI)?.length ?? 0) > 0
    ) {
      endDate = dayjs().add(1, 'week').unix().toString()
    }
  }

  const [untilDate, setUntilDate] = useState<string | null | undefined>(endDate)

  const untilDayJs = dayjs(Number(untilDate) * 1000)

  const onNewEndDatePick = (date: Dayjs | null) => {
    if (date) {
      const newEndDate = date.unix().toString()
      setUntilDate(newEndDate)
      setDateState(false)
    }
  }

  const [dateState, setDateState] = useState(false)

  const openDatePicker = () => {
    setDateState(true)
  }

  const setRecurrenceToEvent = () => {
    props.setOpen(false)
    const recurrence = {
      ...props.recurrence,
      Unit: Number(unit),
      Interval: Number(interval),
      EndDate: untilDate
    }

    if (recurrence.EndDate === null) {
      delete recurrence['EndDate']
    }

    props.setEventRecurrence(recurrence)
  }

  const onClickAway = () => {
    props.setOpen(false)
  }

  const REPEAT_UNIT: Option[] = [
    { label: t('day', { count: Number(interval) }), value: '1' },
    { label: t('week', { count: Number(interval) }), value: '2' },
    { label: t('month', { count: Number(interval) }), value: '3' },
    { label: t('year', { count: Number(interval) }), value: '4' }
  ]

  return (
    <Grid
      container
      direction={'column'}
      sx={{ paddingLeft: 4, paddingRight: 4 }}
    >
      <Box marginLeft={2}>
        <PopupSectionTitle color={'black'}>{t('recurrence')}</PopupSectionTitle>
      </Box>
      <Box height={12} />
      <Grid
        container
        direction={'row'}
        alignItems={'center'}
        marginLeft={2}
        gap={2}
      >
        <PopupRowTitle style={{ cursor: 'default' }}>
          {t('every')}
        </PopupRowTitle>
        <SmallTextField
          id="recurrence-interval"
          autoFocus
          placeholder="1"
          type="number"
          value={interval}
          onChange={onIntervalChange}
          variant="standard"
          inputProps={{
            maxLength: 3,
            style: {
              textAlign: 'center',

              fontSize: 14,

              borderBottom: '0.5px solid',
              borderColor: 'transparent'
            }
          }}
          InputProps={{ disableUnderline: true }}
          sx={{
            padding: '4px',
            minWidth: '40px',
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
              {
                WebkitAppearance: 'none',
                margin: 0
              },
            '& input[type=number]': {
              MozAppearance: 'textfield'
            },
            '&:hover': {
              backgroundColor: theme.palette.surface.hover,
              borderRadius: '8px'
            }
          }}
        />
        <CalendarStyled.CalendarHeaderActionsCell style={{}}>
          <CalendarStyled.RangeSelect
            variant="no-border-bg"
            placeholder="Range"
            size="md"
            showIcon={false}
            options={REPEAT_UNIT}
            value={unit}
            onChange={(e) => {
              setUnit(String(e?.value))
            }}
          />
        </CalendarStyled.CalendarHeaderActionsCell>
      </Grid>
      <Grid
        id="recurrence-until-container"
        container
        direction={'row'}
        justifyContent={'start'}
        alignItems={'center'}
        wrap={'nowrap'}
        gap={2}
      >
        <div style={{ width: 'fit-content' }}>
          <CalendarStyled.RangeSelect
            id="recurrence-until"
            variant="no-border-bg"
            placeholder="Range"
            showIcon={false}
            size="md"
            options={UNTIL}
            value={until}
            onChange={(e) => {
              setUntil(String(e?.label) ?? t('forever'))
              setUntilDate(dayjs(Date.now()).add(1, 'M').unix().toString())
            }}
          />
        </div>
        {until != t('forever') ? (
          <DatePickPopup
            eventHook={props.eventHook}
            onAccept={onNewEndDatePick}
            value={untilDayJs}
            open={dateState}
            setOpen={setDateState}
            instanceStart={untilDayJs.valueOf()}
          >
            <Box
              id="recurrence-end-date-picker"
              sx={{
                padding: '8px',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: theme.palette.surface.hover
                }
              }}
            >
              <PopupRowTitle onClick={openDatePicker}>
                {untilDayJs.format('L')}
              </PopupRowTitle>
            </Box>
          </DatePickPopup>
        ) : (
          <></>
        )}
      </Grid>
      <Box height={16} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%'
        }}
      >
        <Button id="apply-recurrence" size="xs" onClick={setRecurrenceToEvent}>
          {t('apply')}
        </Button>
      </div>
    </Grid>
  )
}

export default function RecurrencePopup(props: RecurrencePopupProps) {
  const [instance, onInstance] = useState<PopupInstance>()
  return (
    <CustomEventRecurrencePopup
      surfaceLevel="secondary"
      padding={4}
      open={props.open}
      onMount={onInstance}
      content={<Content {...props} />}
      placement={'right'}
    >
      {props.children}
    </CustomEventRecurrencePopup>
  )
}
